import type {
  CheckoutPerRoomService,
  CheckoutServiceOption,
  CheckoutServiceRoom,
  ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type {
  APIBookingDraft,
  APIBookingDraftRoom,
  APIBookingDraftServiceOption,
} from '@/scopes/checkout/infrastructure/api/types'
import { isNil, values } from 'ramda'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '../model-mappers/serviceParticipantsMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceOptionMapper?: ServiceOptionMapper
  serviceBannersMapper?: ServiceBannersMapper
  serviceType: ServiceType
}

export const createPerRoomServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutPerRoomService> = (
  dependencies,
) => {
  const {
    serviceName,
    serviceType,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies

  function map(bookingDraft: APIBookingDraft, genericServiceAName?: string) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName || aName === genericServiceAName)

    if (!service)
      return null

    const { aName, options } = service
    const rooms = bookingDraft?.rooms.reduce(
      (acc2: Record<string, CheckoutServiceRoom>, room) => ({
        ...acc2,
        [room.id]: mapRoom(room, options),
      }),
      {},
    )
    const anyRoomHasSelectedValue = values(rooms).some(({ selectedValue }) => selectedValue)

    return {
      id: aName,
      isSelected: anyRoomHasSelectedValue ?? !isNil(anyRoomHasSelectedValue),
      type: serviceType,
      rooms,
      participants: serviceParticipantsMapper.map(service, bookingDraft),
      options: options.reduce(
        (acc2: Record<string, CheckoutServiceOption>, option) => ({
          ...acc2,
          [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
        }),
        {},
      ),
      banners: serviceBannersMapper.map(service),
    }
  }

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom, options: APIBookingDraftServiceOption[]) {
    const filterRoomOptions = options.filter(option =>
      participantIds.every(partId => option.appliesToParticipants.includes(partId)),
    )

    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(participantId => participantId.toString()),
      selectedValue:
        filterRoomOptions
          .filter(({ isSelected }) => isSelected)
          .find(option => participantIds.every(partId => option.assignedParticipants.includes(partId)))
          ?.code
          || null,
      availableOptions: filterRoomOptions.map(({ code }) => code),
    }
  }

  function mapSelection(service: CheckoutPerRoomService) {
    const { rooms } = service
    if (!rooms)
      return []

    return Object.values(rooms ?? {})
      .map(room =>
        room.participantIds.map(participantId => ({
          participantId,
          value: room.selectedValue ?? null,
        })),
      )
      .flat()
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutPerRoomService, step: number): ImpressionViewAncillariesItem[] {
    const seenSelectedOptionValues = new Set<string>()
    const selectedItems = values(service.rooms ?? {}).reduce((items: SelectedItemInput[], room) => {
      if (!room.selectedValue)
        return items

      const option = service.options[room.selectedValue]

      if (!option || seenSelectedOptionValues.has(option.value))
        return items

      seenSelectedOptionValues.add(option.value)
      items.push({
        label: option.label,
        value: option.value,
        price: option.price,
        rawPrice: option.rawPrice,
        badge: option.badge,
      })

      return items
    }, [])

    return mapDefaultSelectedItemToGA4(selectedItems, {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
